import { collection, doc, DocumentReference, Firestore } from 'firebase/firestore';
import { Course } from '../types';
import { getOfferingKey } from './courseIdentity';

export type ReviewIndexData = {
  courseId: string;
  academicYear: string;
  semester: string;
  reviewPath: string;
  reviewerYear: number;
  reviewerFaculty: string;
  createdAt: unknown;
  updatedAt: unknown;
};

export const reviewIndexCollection = (db: Firestore, userId: string) =>
  collection(db, 'users', userId, 'courseReviews');

export const reviewIndexDocument = (
  db: Firestore,
  userId: string,
  course: Pick<Course, 'id' | 'academicYear'> & { courseId?: string }
): DocumentReference =>
  doc(reviewIndexCollection(db, userId), getOfferingKey(course));

export const reviewPathFor = (
  courseId: string,
  academicYear: string,
  userId: string
): string => `courses/${courseId}/offerings/${academicYear}/reviews/${userId}`;

const isValidReviewerYear = (value: unknown): boolean =>
  typeof value === 'number' && [1, 2, 3, 4, 5, 6].includes(value);

const isValidReviewerFaculty = (value: unknown): boolean =>
  typeof value === 'string' && ['lc', 'ia', 'js', 'pg'].includes(value);

export const isCanonicalReviewForPeriod = (
  value: unknown,
  academicYear: string,
  semester: string
): boolean => {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  return data.academicYear === academicYear
    && data.semester === semester
    && isValidReviewerYear(data.reviewerYear)
    && isValidReviewerFaculty(data.reviewerFaculty)
    && data.createdAt !== undefined
    && data.createdAt !== null
    && data.updatedAt !== undefined
    && data.updatedAt !== null;
};

export const isValidReviewIndex = (
  value: unknown,
  documentId: string,
  userId: string,
  academicYear: string,
  semester: string
): value is ReviewIndexData => {
  if (!value || typeof value !== 'object') return false;
  const data = value as Partial<ReviewIndexData>;
  const courseId = typeof data.courseId === 'string' ? data.courseId : '';
  return documentId === `${academicYear}_${courseId}`
    && courseId.length > 0
    && data.academicYear === academicYear
    && data.semester === semester
    && data.reviewPath === reviewPathFor(courseId, academicYear, userId)
    && isValidReviewerYear(data.reviewerYear)
    && isValidReviewerFaculty(data.reviewerFaculty)
    && data.createdAt !== undefined
    && data.createdAt !== null
    && data.updatedAt !== undefined
    && data.updatedAt !== null;
};

export const reviewIndexCourseId = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null;
  const courseId = (value as { courseId?: unknown }).courseId;
  return typeof courseId === 'string' && courseId.length > 0 ? courseId : null;
};
